import { useEffect, useState } from 'react';
import { creditsAPI } from '../../api/client';
import { DEFAULT_STUDIO_URL } from '../../constants';
import type { CreditBalance, UsageResponse } from '../../types';

/** Embeddable usage panel — no page wrapper, no h1. Pass active=false to skip fetching. */
export function UsagePanel({ active = true }: { active?: boolean }) {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [usage, setUsage] = useState<UsageResponse | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [gatewayToken, setGatewayToken] = useState('');

  useEffect(() => {
    if (!active) return;

    const token = localStorage.getItem('clawer_gateway_token') || '';
    setGatewayToken(token);
    if (!token) {
      setLoading(false);
      return;
    }

    Promise.all([
      creditsAPI.balance(DEFAULT_STUDIO_URL, token),
      creditsAPI.usage(DEFAULT_STUDIO_URL, token),
    ])
      .then(([b, u]) => {
        setBalance(b);
        setUsage(u);
      })
      .catch(() => setError('Failed to load usage data'))
      .finally(() => setLoading(false));
  }, [active]);

  const creditPct =
    balance && balance.credit_allowance > 0
      ? Math.min((balance.balance / balance.credit_allowance) * 100, 100)
      : 0;

  const formatDate = (iso: string) => {
    try {
      return new Date(iso).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  };

  return (
    <div className="space-y-6">
      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center py-12">
          <div className="w-12 h-12 border-4 border-edge border-t-ink-secondary rounded-full animate-spin mb-4" />
          <p className="text-ink-secondary">Loading usage data...</p>
        </div>
      )}

      {/* Not managed mode */}
      {!loading && !gatewayToken && (
        <div className="flex flex-col items-center justify-center py-12 text-center gap-3">
          <span className="material-icons-round text-ink-tertiary text-5xl">bar_chart</span>
          <p className="text-ink-secondary text-sm">
            Usage tracking is not set up yet.
          </p>
          <p className="text-xs text-ink-tertiary max-w-xs">
            If you&apos;re using Clawer AI (managed mode), re-provision your instance
            from the Studio dashboard to enable usage tracking.
          </p>
        </div>
      )}

      {/* Error */}
      {!loading && gatewayToken && error && (
        <div className="flex flex-col items-center justify-center py-12">
          <span className="material-icons-round text-red-400 text-5xl mb-3">error_outline</span>
          <p className="text-red-600 mb-4">{error}</p>
        </div>
      )}

      {/* Credit Balance Card */}
      {!loading && !error && balance && (
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider">Credit Balance</h2>
          </div>
          <div className="p-5 space-y-4">
            {/* Big number */}
            <div className="flex items-end gap-2">
              <span className="text-4xl font-bold text-ink">
                {balance.balance.toLocaleString()}
              </span>
              <span className="text-sm text-ink-secondary mb-1">
                / {balance.credit_allowance.toLocaleString()} credits
              </span>
            </div>

            {/* Progress bar */}
            <div className="space-y-1">
              <div className="w-full h-2 rounded-full bg-canvas-secondary overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all ${
                    creditPct >= 90 ? 'bg-red-500' :
                    creditPct >= 70 ? 'bg-amber-500' :
                    'bg-emerald-500'
                  }`}
                  style={{ width: `${creditPct}%` }}
                />
              </div>
              <p className="text-xs text-ink-tertiary">
                {creditPct.toFixed(1)}% used
              </p>
            </div>

            {/* Plan + reset date */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                <span className="material-icons-round text-ink-tertiary text-base">workspace_premium</span>
                <span className="text-sm font-medium text-ink capitalize">{balance.plan}</span>
              </div>
              {balance.current_period_end && (
                <span className="text-xs text-ink-tertiary">
                  Resets {formatDate(balance.current_period_end)}
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Usage Summary */}
      {!loading && !error && usage && (
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider">Usage This Period</h2>
          </div>
          <div className="divide-y divide-edge">
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="material-icons-round text-ink-secondary text-lg">send</span>
                <span className="text-sm text-ink">Total Requests</span>
              </div>
              <span className="text-sm font-semibold text-ink">
                {usage.total_requests.toLocaleString()}
              </span>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <div className="flex items-center gap-2">
                <span className="material-icons-round text-ink-secondary text-lg">toll</span>
                <span className="text-sm text-ink">Credits Used</span>
              </div>
              <span className="text-sm font-semibold text-ink">
                {usage.total_credits_used.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Daily Breakdown Table */}
      {!loading && !error && usage && usage.daily.length > 0 && (
        <div className="rounded-xl bg-white border border-edge overflow-hidden">
          <div className="px-4 py-3 border-b border-edge bg-canvas">
            <h2 className="text-xs font-medium text-ink-tertiary uppercase tracking-wider">Daily Breakdown</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-edge bg-canvas">
                  <th className="text-left px-4 py-2 text-xs font-medium text-ink-tertiary">Date</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-ink-tertiary">Operation</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-ink-tertiary">Model</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-ink-tertiary">Credits</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge">
                {usage.daily.map((row, idx) => (
                  <tr key={idx} className="hover:bg-canvas transition-colors">
                    <td className="px-4 py-2.5 text-ink-secondary whitespace-nowrap">
                      {formatDate(row.date)}
                    </td>
                    <td className="px-4 py-2.5 text-ink capitalize">
                      {row.operation_type.replace(/_/g, ' ')}
                    </td>
                    <td className="px-4 py-2.5 font-mono text-xs text-ink-secondary">
                      {row.model}
                    </td>
                    <td className="px-4 py-2.5 text-right font-medium text-ink">
                      {row.credits_consumed.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {!loading && !error && usage && usage.daily.length === 0 && gatewayToken && (
        <div className="flex flex-col items-center justify-center py-8 text-center gap-2">
          <span className="material-icons-round text-ink-tertiary text-4xl">bar_chart</span>
          <p className="text-ink-secondary text-sm">No usage data for this period yet.</p>
        </div>
      )}
    </div>
  );
}

export function UsageScreen() {
  return (
    <div className="h-full overflow-y-auto glass-scroll">
      <div className="max-w-md sm:max-w-lg md:max-w-2xl mx-auto px-4 pt-6 pb-24 space-y-6">
        <h1 className="text-2xl font-bold text-ink">AI Usage</h1>
        <UsagePanel active />
      </div>
    </div>
  );
}

export default UsageScreen;
